import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import FsiPicker from '../FsiPicker/FsiPicker'
import type { FsiPickerOptions } from '../FsiPicker/FsiPicker'
import type { TreeNode } from '../FsiPickerNode'
import { useServiceContext } from '../../root'
import { usePageContext } from '../../contexts/page-state'
import { UserRole } from '../../types/access'
import { readLastSelectedFolderId, writeLastSelectedFolderId } from '../../utils/lastSelectedFolderStorage'
import { SystemFolderType } from '../../constants/fsi'

interface StudySiteSelectFolderFieldProps {
  isToggled?: boolean
  setIsShown?: (shown: boolean) => void
  hideTrigger?: boolean
  expandAllRoots?: boolean
}

interface FolderPickerOptions {
  showRC?: boolean
  showSystem?: boolean
  isSiteUser?: boolean
  isStudyUser?: boolean
}

const findNodeById = (nodes: TreeNode[], id: number | string): TreeNode | null => {
  for (const node of nodes) {
    if (node.id === id) {
      return node
    }

    if (node.children?.length) {
      const found = findNodeById(node.children, id)
      if (found) {
        return found
      }
    }
  }

  return null
}

const FALLBACK_MODAL_POSITION = { x: 24, y: 110 }
const MODAL_MARGIN = 8
const MODAL_WIDTH_ESTIMATE = 300
const DOCUMENTS_NAV_TEST_ID = 'main-nav-documents'
const SITE_ROOT_NODE_ID = 'my-sites-root'
const STUDY_ROOT_NODE_ID = 'my-studies-root'
const LAST_SELECTED_PARENT_PREFIX = 'setSiteStudySelectedParentFolderId'
const LAST_SELECTED_BRANCH_ROOT_PREFIX = 'setSiteStudySelectedBranchRootId'

const StudySiteSelectFolderField: React.FC<StudySiteSelectFolderFieldProps> = ({
  isToggled = false,
  setIsShown,
  hideTrigger = false,
  expandAllRoots = false
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const serviceContext = useServiceContext()
  const pageContext = usePageContext()

  // Draggable modal state
  const [shown, setShown] = useState(isToggled)
  const [position, setPosition] = useState(FALLBACK_MODAL_POSITION)
  const [dragging, setDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [initialPosition, setInitialPosition] = useState(FALLBACK_MODAL_POSITION)
  // Ref for the ALL_FOLDERS_DOCUMENTS button
  const triggerButtonRef = useRef<HTMLAnchorElement | null>(null)
  const [pickerOpenPathOptions, setPickerOpenPathOptions] = useState<Partial<FsiPickerOptions>>({})

  useEffect(() => {
    setShown(isToggled)
  }, [isToggled])

  // Mouse event handlers for drag
  useEffect(() => {
    if (!shown || !dragging) return

    let rafId: number | null = null

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault()

      // Cancel previous animation frame if it exists
      if (rafId) {
        cancelAnimationFrame(rafId)
      }

      // Use requestAnimationFrame for smooth updates
      rafId = requestAnimationFrame(() => {
        // Calculate how much the mouse has moved from drag start
        const deltaX = e.clientX - dragStart.x
        const deltaY = e.clientY - dragStart.y

        // New position = initial position + delta
        const newX = initialPosition.x + deltaX
        const newY = initialPosition.y + deltaY

        // Apply boundary constraints
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const minTop = 60
        const maxTop = viewportHeight - 100
        const minLeft = 0
        const maxLeft = viewportWidth - 300 // Approximate modal width

        const constrainedX = Math.max(minLeft, Math.min(maxLeft, newX))
        const constrainedY = Math.max(minTop, Math.min(maxTop, newY))

        setPosition({
          x: constrainedX,
          y: constrainedY
        })
      })
    }

    const handleMouseUp = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      setDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: false })
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragging, dragStart, initialPosition, shown])

  const onHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    // Store current mouse position and current modal position
    setDragStart({
      x: e.clientX,
      y: e.clientY
    })
    setInitialPosition(position)
    setDragging(true)
  }

  // Get user role from auth service
  const userRole = serviceContext.service.auth.getRole?.() || ''
  const currentUser = serviceContext.service.auth.getUser?.()
  const isSpecialistOrSponsor = userRole === UserRole.ETMF_SPECIALIST || userRole === UserRole.ETMF_SPONSOR

  const isNodeVisibleForRole = (node: TreeNode): boolean => {
    if (!isSpecialistOrSponsor) {
      return true
    }

    // Keep legacy behavior for specialist/sponsor by hiding explicit no-access nodes.
    // Study/site roots are loaded from role-scoped endpoints and remain visible.
    const nodeAcl = (node as TreeNode & { acl?: string }).acl
    return nodeAcl !== 'NONE'
  }

  const getStoredFolderId = () => {
    const storedFolderId = readLastSelectedFolderId(currentUser)
    return typeof storedFolderId === 'number' && storedFolderId > 0 ? storedFolderId : undefined
  }

  const getParentStorageKey = () => {
    const userId = Number((currentUser as { id?: number | null } | null | undefined)?.id)
    if (Number.isFinite(userId) && userId > 0) {
      return `${LAST_SELECTED_PARENT_PREFIX}:${userId}`
    }
    return LAST_SELECTED_PARENT_PREFIX
  }

  const getBranchRootStorageKey = () => {
    const userId = Number((currentUser as { id?: number | null } | null | undefined)?.id)
    if (Number.isFinite(userId) && userId > 0) {
      return `${LAST_SELECTED_BRANCH_ROOT_PREFIX}:${userId}`
    }
    return LAST_SELECTED_BRANCH_ROOT_PREFIX
  }

  const persistParentFolderId = (parentFolderId: number | undefined) => {
    if (!isSpecialistOrSponsor) {
      return
    }

    const key = getParentStorageKey()
    if (Number.isFinite(parentFolderId) && Number(parentFolderId) > 0) {
      localStorage.setItem(key, String(parentFolderId))
      return
    }

    localStorage.removeItem(key)
  }

  const persistBranchRootId = (branchRootId: string | undefined) => {
    if (!isSpecialistOrSponsor) {
      return
    }

    const key = getBranchRootStorageKey()
    if (branchRootId === SITE_ROOT_NODE_ID || branchRootId === STUDY_ROOT_NODE_ID) {
      localStorage.setItem(key, branchRootId)
      return
    }

    localStorage.removeItem(key)
  }

  const getStoredParentFolderId = (): number | undefined => {
    if (!isSpecialistOrSponsor) {
      return undefined
    }

    const storedParentId = Number(localStorage.getItem(getParentStorageKey()))
    if (Number.isFinite(storedParentId) && storedParentId > 0) {
      return storedParentId
    }

    return undefined
  }

  const getStoredBranchRootId = (): string | undefined => {
    if (!isSpecialistOrSponsor) {
      return undefined
    }

    const storedRootId = localStorage.getItem(getBranchRootStorageKey())
    if (storedRootId === SITE_ROOT_NODE_ID || storedRootId === STUDY_ROOT_NODE_ID) {
      return storedRootId
    }

    return undefined
  }

  const inferParentFolderId = (folder: TreeNode | null | undefined): number | undefined => {
    let current: TreeNode | undefined | null = folder
    let highestNumericIdUnderRoot: number | undefined

    while (current) {
      if (typeof current.id === 'number' && current.id > 0) {
        highestNumericIdUnderRoot = current.id
      }

      if (current.id === SITE_ROOT_NODE_ID || current.id === STUDY_ROOT_NODE_ID) {
        return highestNumericIdUnderRoot
      }
      current = current.parent
    }

    return undefined
  }

  const inferBranchRootId = (folder: TreeNode | null | undefined): string | undefined => {
    let current: TreeNode | undefined | null = folder

    while (current) {
      if (current.id === SITE_ROOT_NODE_ID || current.id === STUDY_ROOT_NODE_ID) {
        return String(current.id)
      }
      current = current.parent
    }

    return undefined
  }

  const persistFolderId = (folderId: number | string | undefined) => {
    const numericId = Number(folderId)
    if (!Number.isFinite(numericId) || numericId <= 0) {
      return
    }

    writeLastSelectedFolderId(currentUser, numericId)
  }

  const buildPickerOpenPathOptions = (): Partial<FsiPickerOptions> => {
    const id = getStoredFolderId()
    if (!id) {
      return {}
    }

    const storedBranchRootId = getStoredBranchRootId()
    const storedParentFolderId = getStoredParentFolderId()
    const openIds = [storedBranchRootId, storedParentFolderId, id].filter(
      (value): value is number | string => value !== undefined && value !== null
    )

    if (openIds.length > 0) {
      return { selectedId: id, leafIds: [id], openIds }
    }

    return { selectedId: id, leafIds: [id], openIds: [id] }
  }

  const getModalPosition = () => {
    const triggerRect = triggerButtonRef.current?.getBoundingClientRect()
    if (triggerRect) {
      return {
        x: triggerRect.left,
        y: Math.max(110, triggerRect.bottom + MODAL_MARGIN)
      }
    }

    if (hideTrigger) {
      const documentsNav = document.querySelector<HTMLElement>(`[data-test-id="${DOCUMENTS_NAV_TEST_ID}"]`)
      const placementTarget = documentsNav ?? document.querySelector<HTMLElement>('.side-nav')

      if (placementTarget) {
        const rect = placementTarget.getBoundingClientRect()
        const maxLeft = Math.max(0, window.innerWidth - MODAL_WIDTH_ESTIMATE)

        return {
          x: Math.min(rect.right + MODAL_MARGIN, maxLeft),
          y: rect.bottom + MODAL_MARGIN
        }
      }
    }

    return FALLBACK_MODAL_POSITION
  }

  // When opening the modal, set its position relative to the trigger button
  const toggleSelectFolder = () => {
    setShown((prev) => {
      const nextShown = !prev
      if (nextShown) {
        setPickerOpenPathOptions(buildPickerOpenPathOptions())
      } else {
        setPickerOpenPathOptions({})
      }
      if (setIsShown) {
        setIsShown(nextShown)
      }
      return nextShown
    })
  }

  // Always align modal with nav-link when shown
  useLayoutEffect(() => {
    if (shown) {
      const modalPosition = getModalPosition()
      setPosition(modalPosition)
      setInitialPosition(modalPosition)
    }
  }, [hideTrigger, shown])

  const closeModal = () => {
    setShown(false)
    if (setIsShown) {
      setIsShown(false)
    }
  }

  /**
   * Get folder picker options based on user role
   * Determines what folder types the user can see in the picker
   */
  const getFolderPickerOptions = (role: string): FolderPickerOptions => {
    switch (role) {
      case UserRole.ADMIN:
        return { showRC: true, showSystem: true }
      case UserRole.AUDITOR:
        return { showRC: true, showSystem: true }
      case UserRole.CONTENT_ADMIN:
        return { showRC: true, showSystem: true }
      case UserRole.ETMF_MANAGER:
        return { showRC: true, showSystem: true }
      case UserRole.ETMF_SITE_USER:
        return { showRC: false, showSystem: false, isSiteUser: true }
      case UserRole.ETMF_SPECIALIST:
        // Legacy parity: specialists can browse RC/System plus study/site trees.
        return { showRC: true, showSystem: true, isSiteUser: true, isStudyUser: true }
      case UserRole.ETMF_SPONSOR:
        return { showRC: true, showSystem: true, isSiteUser: true, isStudyUser: true }
      case UserRole.ETMF_STUDY_USER:
        return { showRC: false, showSystem: false, isStudyUser: true }
      case UserRole.INSPECTOR:
        return { showRC: true, showSystem: false }
      case UserRole.POWER_USER:
        return { showRC: true, showSystem: true }
      case UserRole.RCR_USER:
        return { showRC: true, showSystem: true }
      case UserRole.USER:
        return { showRC: true, showSystem: true }
      default:
        return { showRC: false, showSystem: false }
    }
  }

  /**
   * Handle folder selection from FsiPicker
   * Navigate to the documents browser with selected folder
   */
  const handleFolderSelect = (selectedIds: (number | string)[] | TreeNode, baseNodes?: TreeNode[] | undefined) => {
    const regulatedContentFolderId =
      serviceContext.service.auth.getBrowser?.(SystemFolderType.REGULATED_CONTENT)?.id || 1

    const navigateByFolder = (folder: TreeNode | null | undefined) => {
      if (!folder) {
        return
      }

      persistBranchRootId(inferBranchRootId(folder))
      persistParentFolderId(inferParentFolderId(folder))

      const numericFolderId = Number(folder.id)
      if (Number.isFinite(numericFolderId) && numericFolderId > 0) {
        persistFolderId(numericFolderId)
        navigate(
          `/browser/${numericFolderId}?page=1&type=${folder.systemFolderType || SystemFolderType.REGULATED_CONTENT}`
        )
        return
      }

      // Virtual system nodes (e.g. SYSTEM_DOCUMENTS/REPORTS roots) have string ids.
      if (folder.systemFolderType) {
        navigate(`/browser/${regulatedContentFolderId}?page=1&type=${folder.systemFolderType}`)
      }
    }

    // Handle both array of IDs (checkbox mode) and single node (radio mode)
    if (Array.isArray(selectedIds)) {
      if (baseNodes && selectedIds.length > 0) {
        const selectedFolderId = selectedIds[selectedIds.length - 1]
        const folder = findNodeById(baseNodes, selectedFolderId)
        navigateByFolder(folder)
      }
    } else {
      // Single node selected (not in checkbox mode)
      const folder = selectedIds as TreeNode
      navigateByFolder(folder)
    }
  }

  const folderPickerOptions = getFolderPickerOptions(userRole)

  useEffect(() => {
    if (!isToggled) {
      setPickerOpenPathOptions({})
    } else {
      setPickerOpenPathOptions(buildPickerOpenPathOptions())
    }
  }, [isToggled])

  return (
    <>
      {/* Trigger menu item - only show for staging uploaders */}
      {pageContext.isStagingUploader && !hideTrigger && (
        <li className={`side-nav-item${shown ? ' active' : ''}`}>
          <a onClick={toggleSelectFolder} className="" ref={triggerButtonRef}>
            <span className="fs14">{t('ALL_FOLDERS_DOCUMENTS', 'All Folders & Documents')}</span>
          </a>
        </li>
      )}

      {/* Folder selector modal */}
      {shown && (
        <div
          className="select-folder-wrapper"
          key="select-folder"
          style={{
            left: position.x,
            top: position.y,
            position: 'fixed',
            zIndex: 1000,
            margin: 0
          }}
        >
          <div className="select-folder-custom-style select-folder select-folder-container active">
            <div
              className="header selectfolder"
              onMouseDown={onHeaderMouseDown}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              style={{ cursor: 'move', userSelect: 'none' }}
            >
              <span className="fs14">{t('SELECT_FOLDER', 'Select Folder')}</span>
              <i
                className="close-modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  closeModal()
                }}
              ></i>
            </div>
            <FsiPicker
              options={
                {
                  selector: 'folder',
                  expandAllRoots,
                  ...folderPickerOptions,
                  ...pickerOpenPathOptions
                } as Partial<FsiPickerOptions>
              }
              onSelect={handleFolderSelect}
              isNodeVisible={isNodeVisibleForRole}
              isNodeSelectable={() => true}
              showAddNewFolderModal={false}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default StudySiteSelectFolderField
